import {
  EntryLine,
  LineValidationCategoryPriorityOrder,
  LineValidationState,
  ValidationCategory,
  ValidationState,
  ValidationStatus,
  ValidationType,
} from "./types";
import { LineValidationResponse } from "../../../../../api/validation/validationInterfaces";
import { Line, LineState } from "../../../types";

export const DEFAULT_ROW_COUNT = 10;

export const createEntryLines = (count: number): EntryLine[] =>
  Array.from({ length: count }).map(() => ({
    id: crypto.randomUUID(),
    quantity: 0,
    showQuantityWarning: false,
    quantityWarningMessage: "",
    partNumber: "",
    state: LineState.CREATED,
    showPartNumberWarning: false,
    partNumberWarningMessage: "",
    showPartNumberInfo: false,
    bin: "",
    action: true,
    isEdited: false,
    isValidated: false,
    engineSerialNumber: [],
    showSerialNumberField: false,
    showSerialNumberWarning: false,
    serialNumberWarningMessage: "",
  }));

const validateQuantity = (response: ValidationType[]): ValidationState => {
  const unSuccessfulQuantityValidation = response.find(
    (validation) =>
      validation.category === ValidationCategory.QUANTITY_VALIDATION &&
      validation.status !== ValidationStatus.SUCCESS
  );

  if (
    unSuccessfulQuantityValidation &&
    unSuccessfulQuantityValidation.status === ValidationStatus.FAILURE
  )
    return {
      isValid: false,
      description: "orderLines.quantity.quantityvalidation.failure",
    };
  return {
    isValid: true,
    description: "",
  };
};

const validatePartNumber = (
  response: ValidationType[],
  isHazardousEnabled: boolean
): ValidationState => {
  const unSuccessfulPartNumberValidation = response.find(
    (validation) =>
      validation.category !== ValidationCategory.QUANTITY_VALIDATION &&
      validation.category !== ValidationCategory.PACKAGE_QUANTITY_VALIDATION &&
      validation.category !==
        ValidationCategory.PART_SERIAL_NUMBER_REQ_VALIDATION &&
      validation.category !==
        ValidationCategory.ENGINE_SERIAL_NUMBER_PROVIDED_VALIDATION &&
      (isHazardousEnabled ||
        validation.category !==
          ValidationCategory.PART_NOT_HAZARDOUS_VALIDATION) &&
      validation.status === ValidationStatus.FAILURE
  );

  if (
    unSuccessfulPartNumberValidation &&
    unSuccessfulPartNumberValidation.status === ValidationStatus.FAILURE
  ) {
    if (unSuccessfulPartNumberValidation.upstreamHttpStatus) {
      const { upstreamHttpStatus } = unSuccessfulPartNumberValidation;
      if (
        upstreamHttpStatus === "UNAUTHORIZED" ||
        upstreamHttpStatus === "FORBIDDEN"
      ) {
        return {
          isValid: false,
          description: "orderLines.partNumber.insufficientaccess.failure",
        };
      }
    }

    const validationCategory = unSuccessfulPartNumberValidation.category
      .toLowerCase()
      .replace(/_/g, "");
    return {
      isValid: false,
      description: `orderLines.partNumber.${validationCategory}.failure`,
    };
  }
  return {
    isValid: true,
    description: "",
  };
};

const checkSerialNumberRequired = (response: ValidationType[]): boolean => {
  const serialNumberRequiredValidation = response.find(
    (validation) =>
      validation.category ===
        ValidationCategory.PART_SERIAL_NUMBER_REQ_VALIDATION &&
      validation.status === ValidationStatus.FAILURE
  );
  return !!serialNumberRequiredValidation;
};

const checkEngineSerialNumberProvided = (
  response: ValidationType[]
): boolean => {
  const validation = response.find(
    (v) =>
      v.category ===
        ValidationCategory.ENGINE_SERIAL_NUMBER_PROVIDED_VALIDATION &&
      v.status === ValidationStatus.FAILURE
  );
  return !validation;
};

const validateSerialNumber = (
  response: ValidationType[],
  requiresSerialNumber: boolean
): ValidationState => {
  if (!requiresSerialNumber) {
    return {
      isValid: true,
      description: "",
    };
  }

  if (!checkEngineSerialNumberProvided(response)) {
    return {
      isValid: false,
      description: "orderLines.serialNumber.serialnumbervalidation.failure",
    };
  }
  return {
    isValid: true,
    description: "",
  };
};

export const parseLineValidationResponse = (
  response: LineValidationResponse,
  isHazardousEnabled: boolean,
  isSerialNumberEnabled: boolean = false
): LineValidationState => {
  if (response.status === ValidationStatus.SUCCESS) {
    return {
      isQuantityValid: true,
      quantityFailureMessage: "",
      isPartNumberValid: true,
      partNumberFailureMessage: "",
      showPartNumberInfo: false,
      requiresSerialNumber: false,
      isSerialNumberValid: true,
      serialNumberFailureMessage: "",
    };
  }

  const lineValidationsList = response.validators.flatMap(
    (value) => value.validationReport.validations
  );

  const validationPriorityList: any = LineValidationCategoryPriorityOrder.map(
    (category) => lineValidationsList.find((item) => item.category === category)
  ).filter((item) => item !== undefined);

  const quantityValidation: ValidationState = validateQuantity(
    validationPriorityList
  );
  const isGeoRestricted = lineValidationsList.some(
    (validation) =>
      validation.category === ValidationCategory.GEO_RESTRICT_VALIDATION &&
      validation.status === ValidationStatus.FAILURE &&
      validation.upstreamHttpStatus !== "NOT_FOUND"
  );
  const partNumberValidation: ValidationState = isGeoRestricted
    ? {
        isValid: false,
        description: "orderLines.partNumber.georestrictvalidation.failure",
      }
    : validatePartNumber(validationPriorityList, isHazardousEnabled);

  const partIsHazardous =
    partNumberValidation.description ===
    "orderLines.partNumber.partnothazardousvalidator.failure";

  // Only check serial number requirements when the feature toggle is enabled using ternary operator
  const requiresSerialNumber = isSerialNumberEnabled
    ? checkSerialNumberRequired(lineValidationsList)
    : false;
  const serialNumberValidation: ValidationState = validateSerialNumber(
    lineValidationsList,
    requiresSerialNumber
  );

  return {
    isQuantityValid: quantityValidation.isValid,
    quantityFailureMessage: quantityValidation.description,
    isPartNumberValid: partNumberValidation.isValid,
    partNumberFailureMessage: partNumberValidation.description,
    isValidated: true,
    showPartNumberInfo: partIsHazardous,
    requiresSerialNumber,
    isSerialNumberValid: serialNumberValidation.isValid,
    serialNumberFailureMessage: serialNumberValidation.description,
  } as LineValidationState;
};

const SERIAL_NUMBER_LENGTH = 30;
const SERIAL_NUMBER_PATTERN = /^[A-Za-z0-9]*$/;

export const getSerialNumberMaxLength = (quantity: number): number => {
  if (quantity <= 0) return 0;
  // Each serial is 30 chars, separated by ", " (2 chars)
  return quantity * SERIAL_NUMBER_LENGTH + (quantity - 1) * 2;
};

export const sanitizeSerialNumberInput = (
  input: string,
  quantity: number
): string => {
  if (quantity <= 0) return "";
  // Remove everything except alphanumeric characters and commas
  const cleaned = input.replace(/[^A-Za-z0-9,]/g, "");
  // Split by comma, enforce 30-char limit per segment, limit total segments to quantity
  const segments = cleaned.split(",");
  const processed = segments
    .slice(0, quantity)
    .map((seg) => seg.slice(0, SERIAL_NUMBER_LENGTH));
  return processed.join(", ");
};

export const validateSerialNumbers = (serials: string[]): boolean => {
  const filtered = serials.filter((s) => s.length > 0);
  if (filtered.length === 0) return true;
  return filtered.every(
    (s) => s.length <= SERIAL_NUMBER_LENGTH && SERIAL_NUMBER_PATTERN.test(s)
  );
};

export type EntryLineBase = Pick<
  EntryLine,
  "partNumber" | "quantity" | "bin" | "uniqueLineId" | "engineSerialNumber"
>;

export type ValidatedEntryLine = Omit<EntryLine, "id">;

export const convertLineToEntryLine = (line: Line): EntryLine => ({
  id: line.id,
  quantity: line.quantity,
  partNumber: line.partNumber,
  bin: line.bin,
  state: line.state,
  isValidated: true,
  showQuantityWarning: false,
  quantityWarningMessage: "",
  showPartNumberWarning: false,
  partNumberWarningMessage: "",
  showPartNumberInfo: false,
  isEdited: false,
  action: false,
  isNew: false,
  uniqueLineId: line.uniqueLineId,
  engineSerialNumber: line.engineSerialNumber || [],
  showSerialNumberField: !!(
    line.engineSerialNumber && line.engineSerialNumber.length > 0
  ),
  showSerialNumberWarning: false,
  serialNumberWarningMessage: "",
});

export const convertEntryLineToLine = (
  entryLine: EntryLine
): Omit<Line, "lineNumber"> => ({
  id: entryLine.id,
  partNumber: entryLine.partNumber,
  quantity: entryLine.quantity,
  state: entryLine.state,
  bin: entryLine.bin,
  uniqueLineId: entryLine.uniqueLineId,
  engineSerialNumber: entryLine.engineSerialNumber,
  subLines: [],
});

export const createEntryLinesFromLines = (lines: Line[]): EntryLine[] => [
  ...lines.map(convertLineToEntryLine),
  ...createEntryLines(DEFAULT_ROW_COUNT - lines.length),
];

export const filterLinesWithSuccessfulValidation = (
  rows: EntryLine[]
): EntryLine[] =>
  rows.filter(
    (row) =>
      !row.showQuantityWarning &&
      (!row.showPartNumberWarning || row.showPartNumberInfo) &&
      !row.showSerialNumberWarning &&
      row.isValidated
  );
